/**
 * categories.ts - Category management UI for tournaments
 *
 * Handles category CRUD operations and rendering
 */

import type { Category, Tournament } from '../../types/api';
import { t } from '../utils/i18n-helper';
import { escapeHtml } from '../utils/security';

// Standard category templates
const STANDARD_CATEGORIES = {
  age: [
    { name: 'U8', type: 'age' as const, maxAge: 7 },
    { name: 'U10', type: 'age' as const, maxAge: 9 },
    { name: 'U12', type: 'age' as const, maxAge: 11 },
    { name: 'U14', type: 'age' as const, maxAge: 13 },
    { name: 'U16', type: 'age' as const, maxAge: 15 },
    { name: 'U18', type: 'age' as const, maxAge: 17 },
    { name: 'U20', type: 'age' as const, maxAge: 19 },
    { name: '50+', type: 'age' as const, minAge: 50 },
    { name: '65+', type: 'age' as const, minAge: 65 },
  ],
  gender: [
    { name: 'Women', type: 'gender' as const, genderFilter: 'F' as const },
  ],
  title: [
    { name: 'Non-titled', type: 'title' as const, titleFilter: 'none' },
  ],
};

// Cache for tournament categories
const categoryCache = new Map<string, Category[]>();

/**
 * Load categories for a tournament
 */
export async function loadCategories(tournamentId: string): Promise<Category[]> {
  try {
    const response = await fetch(`/api/tournaments/${tournamentId}/categories`, { credentials: 'include' });
    if (!response.ok) {
      throw new Error('Failed to load categories');
    }
    const categories = await response.json();
    categoryCache.set(tournamentId, categories);
    return categories;
  } catch (error) {
    console.error('Error loading categories:', error);
    return [];
  }
}

/**
 * Get cached categories for a tournament
 */
export function getCachedCategories(tournamentId: string): Category[] {
  return categoryCache.get(tournamentId) || [];
}

/**
 * Create a category
 */
export async function createCategory(
  tournamentId: string,
  category: Omit<Category, 'id' | 'tournamentId' | 'createdAt'>
): Promise<Category | null> {
  try {
    const response = await fetch(`/api/tournaments/${tournamentId}/categories`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(category),
    });
    if (!response.ok) {
      throw new Error('Failed to create category');
    }
    const newCategory = await response.json();
    // Update cache
    const cached = categoryCache.get(tournamentId) || [];
    categoryCache.set(tournamentId, [...cached, newCategory]);
    return newCategory;
  } catch (error) {
    console.error('Error creating category:', error);
    return null;
  }
}

/**
 * Delete a category
 */
export async function deleteCategory(tournamentId: string, categoryId: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/tournaments/${tournamentId}/categories/${categoryId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      throw new Error('Failed to delete category');
    }
    // Update cache
    const cached = categoryCache.get(tournamentId) || [];
    categoryCache.set(tournamentId, cached.filter(c => c.id !== categoryId));
    return true;
  } catch (error) {
    console.error('Error deleting category:', error);
    return false;
  }
}

/**
 * Render category type label
 */
function getCategoryTypeLabel(category: Category): string {
  switch (category.type) {
    case 'age':
      if (category.minAge !== undefined && category.maxAge !== undefined) {
        return `${category.minAge}-${category.maxAge} ${t('category.years', 'years')}`;
      } else if (category.maxAge !== undefined) {
        return `≤${category.maxAge} ${t('category.years', 'years')}`;
      } else if (category.minAge !== undefined) {
        return `≥${category.minAge} ${t('category.years', 'years')}`;
      }
      return t('category.typeAge', 'Age');
    case 'gender':
      return category.genderFilter === 'F' ? t('player.female', 'Female') : t('player.male', 'Male');
    case 'title':
      if (category.titleFilter === 'none') return t('category.nonTitled', 'Non-titled');
      if (category.titleFilter === 'any') return t('category.titled', 'Titled');
      return category.titleFilter || t('category.typeTitle', 'Title');
    case 'custom':
      return t('category.typeCustom', 'Custom');
    default:
      return '';
  }
}

/**
 * Render the categories section for tournament settings
 */
export function renderCategoriesSection(tournament: Tournament, categories: Category[]): string {
  const categoryRows = categories.length > 0 ? categories.map(cat => `
    <div class="category-item" data-category-id="${cat.id}">
      <span class="category-name">${escapeHtml(cat.name)}</span>
      <span class="category-type-badge category-type-${cat.type}">${getCategoryTypeLabel(cat)}</span>
      <button class="btn-icon btn-icon-danger" onclick="deleteCategory('${tournament.id}', '${cat.id}')" title="${t('category.delete', 'Delete')}">🗑</button>
    </div>
  `).join('') : `<p class="no-categories">${t('category.noCategories', 'No categories defined yet')}</p>`;

  return `
    <div class="categories-management" id="categories-section-content-${tournament.id}">
      <div class="categories-list">
        ${categoryRows}
      </div>

      <div class="categories-actions">
        <div class="category-template-dropdown">
          <select id="category-template-${tournament.id}" class="category-template-select">
            <option value="">${t('category.addFromTemplate', 'Add from template...')}</option>
            <optgroup label="${t('category.typeAge', 'Age')}">
              ${STANDARD_CATEGORIES.age.map(cat => `
                <option value="age:${cat.name}">${cat.name}</option>
              `).join('')}
            </optgroup>
            <optgroup label="${t('category.typeGender', 'Gender')}">
              ${STANDARD_CATEGORIES.gender.map(cat => `
                <option value="gender:${cat.name}">${cat.name}</option>
              `).join('')}
            </optgroup>
            <optgroup label="${t('category.typeTitle', 'Title')}">
              ${STANDARD_CATEGORIES.title.map(cat => `
                <option value="title:${cat.name}">${cat.name}</option>
              `).join('')}
            </optgroup>
          </select>
          <button class="btn btn-secondary btn-small" onclick="addCategoryFromTemplate('${tournament.id}')">${t('category.add', 'Add')}</button>
        </div>
        <button class="btn btn-secondary btn-small" onclick="openCustomCategoryModal('${tournament.id}')">${t('category.addCustom', 'Add Custom')}</button>
      </div>
    </div>
  `;
}

/**
 * Add a category from a template
 */
export async function addCategoryFromTemplate(tournamentId: string): Promise<void> {
  const select = document.getElementById(`category-template-${tournamentId}`) as HTMLSelectElement;
  if (!select || !select.value) return;

  const [type, name] = select.value.split(':');

  let categoryData: Omit<Category, 'id' | 'tournamentId' | 'createdAt'> | null = null;

  if (type === 'age') {
    const template = STANDARD_CATEGORIES.age.find(c => c.name === name);
    if (template) {
      categoryData = { ...template };
    }
  } else if (type === 'gender') {
    const template = STANDARD_CATEGORIES.gender.find(c => c.name === name);
    if (template) {
      categoryData = { ...template };
    }
  } else if (type === 'title') {
    const template = STANDARD_CATEGORIES.title.find(c => c.name === name);
    if (template) {
      categoryData = { ...template };
    }
  }

  if (categoryData) {
    const result = await createCategory(tournamentId, categoryData);
    if (result) {
      // Refresh the categories section
      await refreshCategoriesSection(tournamentId);
      select.value = '';
    }
  }
}

/**
 * Open custom category modal
 */
export function openCustomCategoryModal(tournamentId: string): void {
  // Create modal if it doesn't exist
  let modal = document.getElementById('custom-category-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'custom-category-modal';
    modal.className = 'modal-overlay';
    document.body.appendChild(modal);
  }

  modal.innerHTML = `
    <div class="modal-content modal-small">
      <div class="modal-header">
        <h3>${t('category.addCustom', 'Add Custom Category')}</h3>
        <button class="modal-close" onclick="closeCustomCategoryModal()">&times;</button>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label>${t('category.name', 'Category Name')}</label>
          <input type="text" id="custom-category-name" class="form-control" placeholder="${t('category.namePlaceholder', 'e.g., Junior')}">
        </div>
        <div class="form-group">
          <label>${t('category.type', 'Type')}</label>
          <select id="custom-category-type" class="form-control" onchange="updateCustomCategoryFields()">
            <option value="custom">${t('category.typeCustom', 'Custom (manual assignment)')}</option>
            <option value="age">${t('category.typeAge', 'Age-based')}</option>
            <option value="gender">${t('category.typeGender', 'Gender-based')}</option>
            <option value="title">${t('category.typeTitle', 'Title-based')}</option>
          </select>
        </div>
        <div id="custom-category-age-fields" style="display: none;">
          <div class="form-row">
            <div class="form-group">
              <label>${t('category.minAge', 'Min Age')}</label>
              <input type="number" id="custom-category-min-age" class="form-control" min="0">
            </div>
            <div class="form-group">
              <label>${t('category.maxAge', 'Max Age')}</label>
              <input type="number" id="custom-category-max-age" class="form-control" min="0">
            </div>
          </div>
        </div>
        <div id="custom-category-gender-fields" style="display: none;">
          <div class="form-group">
            <label>${t('category.genderFilter', 'Gender')}</label>
            <select id="custom-category-gender" class="form-control">
              <option value="F">${t('player.female', 'Female')}</option>
              <option value="M">${t('player.male', 'Male')}</option>
            </select>
          </div>
        </div>
        <div id="custom-category-title-fields" style="display: none;">
          <div class="form-group">
            <label>${t('category.titleFilter', 'Title Filter')}</label>
            <select id="custom-category-title" class="form-control">
              <option value="none">${t('category.nonTitled', 'Non-titled players')}</option>
              <option value="any">${t('category.titled', 'Any title')}</option>
              <option value="GM">GM</option>
              <option value="IM">IM</option>
              <option value="FM">FM</option>
              <option value="CM">CM</option>
              <option value="WGM">WGM</option>
              <option value="WIM">WIM</option>
              <option value="WFM">WFM</option>
              <option value="WCM">WCM</option>
            </select>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="closeCustomCategoryModal()">${t('common.cancel', 'Cancel')}</button>
        <button class="btn btn-primary" onclick="saveCustomCategory('${tournamentId}')">${t('common.save', 'Save')}</button>
      </div>
    </div>
  `;

  modal.style.display = 'flex';
}

/**
 * Close custom category modal
 */
export function closeCustomCategoryModal(): void {
  const modal = document.getElementById('custom-category-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Update custom category form fields based on type
 */
export function updateCustomCategoryFields(): void {
  const typeSelect = document.getElementById('custom-category-type') as HTMLSelectElement;
  const ageFields = document.getElementById('custom-category-age-fields');
  const genderFields = document.getElementById('custom-category-gender-fields');
  const titleFields = document.getElementById('custom-category-title-fields');

  if (!typeSelect || !ageFields || !genderFields || !titleFields) return;

  ageFields.style.display = 'none';
  genderFields.style.display = 'none';
  titleFields.style.display = 'none';

  switch (typeSelect.value) {
    case 'age':
      ageFields.style.display = 'block';
      break;
    case 'gender':
      genderFields.style.display = 'block';
      break;
    case 'title':
      titleFields.style.display = 'block';
      break;
  }
}

/**
 * Save custom category
 */
export async function saveCustomCategory(tournamentId: string): Promise<void> {
  const nameInput = document.getElementById('custom-category-name') as HTMLInputElement;
  const typeSelect = document.getElementById('custom-category-type') as HTMLSelectElement;

  if (!nameInput || !typeSelect || !nameInput.value.trim()) {
    alert(t('category.nameRequired', 'Please enter a category name'));
    return;
  }

  const categoryData: Omit<Category, 'id' | 'tournamentId' | 'createdAt'> = {
    name: nameInput.value.trim(),
    type: typeSelect.value as Category['type'],
  };

  // Add type-specific fields
  if (typeSelect.value === 'age') {
    const minAgeInput = document.getElementById('custom-category-min-age') as HTMLInputElement;
    const maxAgeInput = document.getElementById('custom-category-max-age') as HTMLInputElement;
    if (minAgeInput.value) categoryData.minAge = parseInt(minAgeInput.value, 10);
    if (maxAgeInput.value) categoryData.maxAge = parseInt(maxAgeInput.value, 10);
  } else if (typeSelect.value === 'gender') {
    const genderSelect = document.getElementById('custom-category-gender') as HTMLSelectElement;
    categoryData.genderFilter = genderSelect.value as 'M' | 'F';
  } else if (typeSelect.value === 'title') {
    const titleSelect = document.getElementById('custom-category-title') as HTMLSelectElement;
    categoryData.titleFilter = titleSelect.value;
  }

  const result = await createCategory(tournamentId, categoryData);
  if (result) {
    closeCustomCategoryModal();
    await refreshCategoriesSection(tournamentId);
  }
}

/**
 * Refresh the categories section
 */
export async function refreshCategoriesSection(tournamentId: string): Promise<void> {
  const categories = await loadCategories(tournamentId);
  const container = document.getElementById(`categories-section-content-${tournamentId}`);
  if (container) {
    // Get the tournament from window
    const tournament = (window as any).currentTournament;
    if (tournament) {
      const parent = container.parentElement;
      if (parent) {
        parent.innerHTML = renderCategoriesSection(tournament, categories);
      }
    }
  }

  // Also refresh standings category filter if it exists
  if ((window as any).refreshStandingsCategoryFilter) {
    (window as any).refreshStandingsCategoryFilter(tournamentId, categories);
  }
}

/**
 * Delete a category with confirmation
 */
export async function deleteCategoryWithConfirm(tournamentId: string, categoryId: string): Promise<void> {
  if (!confirm(t('category.deleteConfirm', 'Are you sure you want to delete this category?'))) {
    return;
  }

  const success = await deleteCategory(tournamentId, categoryId);
  if (success) {
    await refreshCategoriesSection(tournamentId);
  }
}

/**
 * Calculate age from birth date relative to a reference date
 */
function calculateAge(birthDate: string, referenceDate: Date = new Date()): number | undefined {
  if (!birthDate) return undefined;

  const birth = new Date(birthDate);
  if (isNaN(birth.getTime())) return undefined;

  let age = referenceDate.getFullYear() - birth.getFullYear();
  const monthDiff = referenceDate.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

/**
 * Check if a player matches a category's criteria
 */
function playerMatchesCategory(
  player: { birthDate?: string; gender?: 'M' | 'F'; title?: string; categories?: string[] },
  category: Category,
  referenceDate: Date = new Date()
): boolean {
  // Check if manually assigned
  if (player.categories?.includes(category.id)) return true;

  switch (category.type) {
    case 'age': {
      if (!player.birthDate) return false;
      const age = calculateAge(player.birthDate, referenceDate);
      if (age === undefined) return false;
      if (category.minAge !== undefined && age < category.minAge) return false;
      if (category.maxAge !== undefined && age > category.maxAge) return false;
      return true;
    }

    case 'gender': {
      if (!category.genderFilter) return true;
      return player.gender === category.genderFilter;
    }

    case 'title': {
      const playerTitle = player.title?.trim().toUpperCase() || '';
      const hasTitle = playerTitle !== '';

      if (category.titleFilter === 'none') {
        return !hasTitle;
      } else if (category.titleFilter === 'any') {
        return hasTitle;
      } else if (category.titleFilter) {
        return playerTitle === category.titleFilter.toUpperCase();
      }
      return true;
    }

    case 'custom':
      return false; // Only manual assignment

    default:
      return false;
  }
}

/**
 * Get categories a player belongs to (both auto and manual)
 */
export function getPlayerCategories(
  player: { birthDate?: string; gender?: 'M' | 'F'; title?: string; categories?: string[] },
  categories: Category[],
  referenceDate: Date = new Date()
): Category[] {
  return categories.filter(category => playerMatchesCategory(player, category, referenceDate));
}

/**
 * Update category badges for all players in the list
 */
export async function updatePlayerCategoryBadges(tournamentId: string): Promise<void> {
  const tournament = (window as any).currentTournament;
  if (!tournament) return;

  const categories = await loadCategories(tournamentId);
  if (categories.length === 0) return;

  const referenceDate = tournament.startDate ? new Date(tournament.startDate) : new Date();

  tournament.players.forEach((player: any) => {
    const badgeContainer = document.getElementById(`player-categories-${tournamentId}-${player.id}`);
    if (!badgeContainer) return;

    const playerCategories = getPlayerCategories(player, categories, referenceDate);
    if (playerCategories.length === 0) {
      badgeContainer.innerHTML = '';
      return;
    }

    badgeContainer.innerHTML = playerCategories.map(cat =>
      `<span class="player-category-badge">${escapeHtml(cat.name)}</span>`
    ).join('');
  });
}

/**
 * Initialize categories section content
 */
export async function initCategoriesSection(tournamentId: string): Promise<void> {
  const container = document.getElementById(`categories-content-${tournamentId}`);
  if (!container) return;

  // Show loading state
  container.innerHTML = `<p class="loading-text">${t('common.loading', 'Loading...')}</p>`;

  try {
    const categories = await loadCategories(tournamentId);
    const tournament = (window as any).currentTournament;

    if (tournament) {
      container.innerHTML = renderCategoriesSection(tournament, categories);
    }
  } catch (error) {
    console.error('Error initializing categories section:', error);
    container.innerHTML = `<p class="error-text">${t('category.loadError', 'Failed to load categories')}</p>`;
  }
}

/**
 * Initialize categories in the add player form with auto-selection
 */
export async function initAddPlayerCategories(tournamentId: string): Promise<void> {
  const container = document.getElementById(`add-player-categories-${tournamentId}`);
  if (!container) return;

  const categories = await loadCategories(tournamentId);
  if (categories.length === 0) {
    container.style.display = 'none';
    return;
  }

  // Render category pills
  container.style.display = 'flex';
  container.innerHTML = `
    <label class="categories-label">${t('player.categories', 'Categories')}:</label>
    <div class="player-categories-checkboxes">
      ${categories.map(cat => `
        <label class="category-checkbox" data-category-id="${cat.id}" data-category-type="${cat.type}">
          <input type="checkbox" name="add-player-category" value="${cat.id}">
          <span>${escapeHtml(cat.name)}</span>
        </label>
      `).join('')}
    </div>
  `;

  // Set up auto-selection listeners
  const tournament = (window as any).currentTournament;
  const referenceDate = tournament?.startDate ? new Date(tournament.startDate) : new Date();

  const birthDateInput = document.getElementById(`player-birth-date-${tournamentId}`) as HTMLInputElement;
  const genderSelect = document.getElementById(`player-gender-${tournamentId}`) as HTMLSelectElement;
  const titleSelect = document.getElementById(`player-title-${tournamentId}`) as HTMLSelectElement;

  const updateAutoSelection = () => {
    const player = {
      birthDate: birthDateInput?.value || undefined,
      gender: (genderSelect?.value as 'M' | 'F') || undefined,
      title: titleSelect?.value || undefined
    };

    // Update each category checkbox based on auto-match
    categories.forEach(category => {
      const checkbox = container.querySelector(`input[value="${category.id}"]`) as HTMLInputElement;
      if (!checkbox) return;

      // Only auto-select non-custom categories
      if (category.type !== 'custom') {
        const matches = playerMatchesCategory(player, category, referenceDate);
        checkbox.checked = matches;
      }
    });
  };

  // Listen for changes
  birthDateInput?.addEventListener('change', updateAutoSelection);
  genderSelect?.addEventListener('change', updateAutoSelection);
  titleSelect?.addEventListener('change', updateAutoSelection);

  // Initial update
  updateAutoSelection();
}

/**
 * Get selected category IDs from add player form
 */
export function getAddPlayerCategoryIds(tournamentId: string): string[] {
  const container = document.getElementById(`add-player-categories-${tournamentId}`);
  if (!container) return [];

  const checkboxes = container.querySelectorAll('input[name="add-player-category"]:checked') as NodeListOf<HTMLInputElement>;
  return Array.from(checkboxes).map(cb => cb.value);
}

/**
 * Clear category selections in add player form
 */
export function clearAddPlayerCategories(tournamentId: string): void {
  const container = document.getElementById(`add-player-categories-${tournamentId}`);
  if (!container) return;

  const checkboxes = container.querySelectorAll('input[name="add-player-category"]') as NodeListOf<HTMLInputElement>;
  checkboxes.forEach(cb => cb.checked = false);
}

// Store active category filter per tournament
const activePlayerCategoryFilter = new Map<string, string>();

/**
 * Initialize category filter pills for player list
 */
export async function initPlayerCategoryFilter(tournamentId: string): Promise<void> {
  const container = document.getElementById(`player-category-filter-${tournamentId}`);
  if (!container) return;

  const categories = await loadCategories(tournamentId);
  if (categories.length === 0) {
    container.style.display = 'none';
    return;
  }

  const selectedCategoryId = activePlayerCategoryFilter.get(tournamentId) || '';

  container.style.display = 'flex';
  container.innerHTML = categories.map(cat => `
    <button type="button" class="player-category-pill ${selectedCategoryId === cat.id ? 'active' : ''}"
      data-category-id="${cat.id}"
      onclick="filterPlayersByCategory('${tournamentId}', '${cat.id}')">
      ${escapeHtml(cat.name)}
    </button>
  `).join('');
}

/**
 * Filter players by category
 */
export function filterPlayersByCategory(tournamentId: string, categoryId: string): void {
  // Toggle: if clicking on already selected category, deselect it
  const currentFilter = activePlayerCategoryFilter.get(tournamentId);
  if (categoryId && categoryId === currentFilter) {
    categoryId = ''; // Deselect
  }

  // Store the filter selection
  if (categoryId) {
    activePlayerCategoryFilter.set(tournamentId, categoryId);
  } else {
    activePlayerCategoryFilter.delete(tournamentId);
  }

  // Update pill active states
  const container = document.getElementById(`player-category-filter-${tournamentId}`);
  if (container) {
    container.querySelectorAll('.player-category-pill').forEach(pill => {
      const pillCatId = pill.getAttribute('data-category-id');
      pill.classList.toggle('active', pillCatId === categoryId);
    });
  }

  // Get tournament and categories from cache
  const tournament = (window as any).currentTournament;
  const categories = getCachedCategories(tournamentId);
  const selectedCategory = categoryId ? categories.find(c => c.id === categoryId) : null;

  const list = document.getElementById(`player-list-ul-${tournamentId}`);
  if (!list) return;

  const rows = list.querySelectorAll('.player-row');
  const referenceDate = tournament?.startDate ? new Date(tournament.startDate) : new Date();

  // Also consider text filter
  const textFilter = document.getElementById(`player-filter-${tournamentId}`) as HTMLInputElement;
  const textQuery = textFilter?.value?.toLowerCase().trim() || '';

  let visibleCount = 0;

  rows.forEach((row) => {
    const li = row as HTMLElement;
    const playerId = li.dataset.playerid;
    if (!playerId || !tournament) {
      li.style.display = '';
      visibleCount++;
      return;
    }

    // Check text filter
    const name = li.dataset.name || '';
    const rating = li.dataset.rating || '';
    const federation = li.dataset.federation || '';
    const club = li.dataset.club || '';
    const title = li.dataset.title || '';

    const matchesText = !textQuery ||
      name.includes(textQuery) ||
      rating.includes(textQuery) ||
      federation.includes(textQuery) ||
      club.includes(textQuery) ||
      title.includes(textQuery);

    // Check category filter
    let matchesCategory = true;
    if (selectedCategory) {
      const player = tournament.players.find((p: any) => p.id === playerId);
      if (player) {
        const playerCategories = getPlayerCategories(player, categories, referenceDate);
        matchesCategory = playerCategories.some(c => c.id === categoryId);
      }
    }

    const isVisible = matchesText && matchesCategory;
    li.style.display = isVisible ? '' : 'none';
    if (isVisible) visibleCount++;
  });

  // Update visible count
  const countSpan = document.querySelector(`#players-tab-${tournamentId} .player-count`);
  if (countSpan) {
    const totalCount = rows.length;
    if (textQuery || categoryId) {
      countSpan.textContent = `${visibleCount}/${totalCount}`;
    } else {
      countSpan.textContent = `${totalCount} ${totalCount === 1 ? t('player.player', 'player') : t('player.players', 'players')}`;
    }
  }
}

/**
 * Get active player category filter for a tournament
 */
export function getActivePlayerCategoryFilter(tournamentId: string): string | undefined {
  return activePlayerCategoryFilter.get(tournamentId);
}

// Export functions to window for onclick handlers
window.addCategoryFromTemplate = addCategoryFromTemplate;
window.openCustomCategoryModal = openCustomCategoryModal;
window.closeCustomCategoryModal = closeCustomCategoryModal;
window.updateCustomCategoryFields = updateCustomCategoryFields;
window.saveCustomCategory = saveCustomCategory;
window.deleteCategory = deleteCategoryWithConfirm;
window.loadCategories = loadCategories;
window.getCachedCategories = getCachedCategories;
window.initCategoriesSection = initCategoriesSection;
window.updatePlayerCategoryBadges = updatePlayerCategoryBadges;
window.getPlayerCategories = getPlayerCategories;
window.initAddPlayerCategories = initAddPlayerCategories;
window.getAddPlayerCategoryIds = getAddPlayerCategoryIds;
window.clearAddPlayerCategories = clearAddPlayerCategories;
window.initPlayerCategoryFilter = initPlayerCategoryFilter;
window.filterPlayersByCategory = filterPlayersByCategory;
window.getActivePlayerCategoryFilter = getActivePlayerCategoryFilter;

// Type declarations
declare global {
  interface Window {
    addCategoryFromTemplate: typeof addCategoryFromTemplate;
    openCustomCategoryModal: typeof openCustomCategoryModal;
    closeCustomCategoryModal: typeof closeCustomCategoryModal;
    updateCustomCategoryFields: typeof updateCustomCategoryFields;
    saveCustomCategory: typeof saveCustomCategory;
    deleteCategory: typeof deleteCategoryWithConfirm;
    loadCategories: typeof loadCategories;
    getCachedCategories: typeof getCachedCategories;
    initCategoriesSection: typeof initCategoriesSection;
    updatePlayerCategoryBadges: typeof updatePlayerCategoryBadges;
    getPlayerCategories: typeof getPlayerCategories;
    initAddPlayerCategories: typeof initAddPlayerCategories;
    getAddPlayerCategoryIds: typeof getAddPlayerCategoryIds;
    clearAddPlayerCategories: typeof clearAddPlayerCategories;
    initPlayerCategoryFilter: typeof initPlayerCategoryFilter;
    filterPlayersByCategory: typeof filterPlayersByCategory;
    getActivePlayerCategoryFilter: typeof getActivePlayerCategoryFilter;
    currentTournament?: Tournament;
    refreshStandingsCategoryFilter?: (tournamentId: string, categories: Category[]) => void;
  }
}
